import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const TimingChainBeltReplacement = () => {
  const timingFeatures = [
    'All European Makes',
    'OEM Parts Available',
    'Preventive Maintenance',
    'Emergency Service',
    'Warranty Protection',
    'Free Inspection'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const warningSignsChain = [
    'Rattling noise from engine on startup',
    'Engine rough idle',
    'Metal shavings in oil',
    'Check engine light',
    'Engine won\'t start or misfires'
  ];

  const warningSignsBelt = [
    'High-pitched squealing noise',
    'Visible cracks on belt',
    'Engine overheating',
    'Oil leaks near timing cover',
    'Ticking noise from engine'
  ];

  const commonModels = [
    {
      brand: 'BMW',
      models: 'N20, N26, N63, N74 engines',
      system: 'Chain',
      interval: '120,000 miles',
      risk: 'High',
      note: 'Known for guide rail failures'
    },
    {
      brand: 'Audi/VW',
      models: '2.0T TSI, 3.0T TFSI',
      system: 'Chain/Belt',
      interval: '80,000-120,000 miles',
      risk: 'Medium',
      note: 'Tensioner failures common'
    },
    {
      brand: 'Mercedes',
      models: 'M271, M272, M273',
      system: 'Chain',
      interval: '100,000+ miles',
      risk: 'High',
      note: 'Balance shaft issues'
    },
    {
      brand: 'Porsche',
      models: '911, Cayenne, Panamera',
      system: 'Chain',
      interval: 'Lifetime (inspect)',
      risk: 'Low',
      note: 'IMS bearing concerns on older models'
    },
    {
      brand: 'Mini Cooper',
      models: 'R56 N14/N18',
      system: 'Chain',
      interval: '100,000 miles',
      risk: 'Very High',
      note: 'Premature chain stretch'
    },
    {
      brand: 'Volvo',
      models: '5-cylinder engines',
      system: 'Belt',
      interval: '100,000 miles',
      risk: 'Medium',
      note: 'Critical to replace on time'
    }
  ];

  const serviceProcess = [
    {
      step: '1',
      title: 'Initial Inspection',
      description: 'Comprehensive engine diagnostic and visual inspection'
    },
    {
      step: '2',
      title: 'Quote & Approval',
      description: 'Detailed estimate with parts and labor breakdown'
    },
    {
      step: '3',
      title: 'Parts Ordering',
      description: 'OEM or quality aftermarket parts sourced'
    },
    {
      step: '4',
      title: 'Replacement Service',
      description: 'Complete timing system replacement with all components'
    },
    {
      step: '5',
      title: 'Testing & Verification',
      description: 'Engine timing verification and test drive'
    },
    {
      step: '6',
      title: 'Warranty & Follow-up',
      description: '2-year/24,000-mile warranty on parts and labor'
    }
  ];

  const pricingGuide = [
    {
      category: 'Timing Belt Replacement',
      priceRange: '$800 - $1,500',
      includes: [
        'Timing belt',
        'Water pump (recommended)',
        'Tensioners and pulleys',
        'Coolant flush',
        'Labor'
      ]
    },
    {
      category: 'Timing Chain Replacement',
      priceRange: '$2,000 - $4,500',
      includes: [
        'Timing chain(s)',
        'Guides and rails',
        'Tensioners',
        'Gaskets and seals',
        'Oil change',
        'Labor'
      ]
    },
    {
      category: 'Additional Services',
      priceRange: 'Varies',
      includes: [
        'Cam/Crank sensors',
        'VVT solenoids',
        'Oil pump chain',
        'Engine mounts',
        'Thermostat'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Timing Chain & Belt Replacement Dallas | European Car Specialists | Paradise-Performance</title>
        <meta name="description" content="Expert timing chain and belt replacement for BMW, Mercedes, Audi, VW, and all European cars in Dallas. Prevent engine damage with preventive maintenance. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/timing-chain-belt-replacement" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Timing Chain & Belt Replacement",
            "url": "https://paradise-performance.com/services/timing-chain-belt-replacement",
            "description": "Expert timing chain and belt replacement for European vehicles in Dallas.",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth"
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Timing System"
        brandColor="#DC2626"
        tagline="Critical Engine Protection Service"
        description="Don't risk catastrophic engine failure. Expert timing chain and belt replacement for all European vehicles."
        features={timingFeatures}
        heroImage="/assets/assets/images/new/timing-chain-belt/paradise-performance-clients16.webp"
        heroImageAlt="Timing Chain and Belt Service"
      />

      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Introduction */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              The Difference Between Timing Chains and Belts
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-4 flex items-center">
                  <Settings className="w-6 h-6 text-blue-600 mr-2" />
                  Timing Chains
                </h3>
                <p className="text-gray-700 mb-4">
                  Metal chains designed to last the engine's lifetime, but modern engines with extended 
                  oil change intervals often experience premature wear. Common in BMW, Mercedes, and newer Audis.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Typically last 80,000-120,000 miles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>More expensive to replace ($2,000-$4,500)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Fail gradually with warning signs</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-4 flex items-center">
                  <Wrench className="w-6 h-6 text-orange-600 mr-2" />
                  Timing Belts
                </h3>
                <p className="text-gray-700 mb-4">
                  Rubber belts that require regular replacement as preventive maintenance. Common in older 
                  VWs, Audis, and some Volvos. Silent operation but can fail suddenly.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Replace every 60,000-100,000 miles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Less expensive to replace ($800-$1,500)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
                    <span>Can fail suddenly without warning</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Image Gallery */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="/assets/assets/images/new/timing-chain-belt/paradise-performance-clients17.webp"
                alt="Timing chain service in progress"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
                <div className="p-8 max-w-lg">
                  <h3 className="text-3xl font-bold text-white mb-2">Preventive Maintenance Saves Engines</h3>
                  <p className="text-white/90">A broken timing belt or chain can cause catastrophic engine damage costing thousands. Regular inspection and timely replacement is critical.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Common Models Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Common European Models & Their Timing Systems</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Models/Engines</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Interval</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {commonModels.map((model, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{model.brand}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{model.models}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{model.system}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{model.interval}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          model.risk === 'High' || model.risk === 'Very High' ? 'bg-red-100 text-red-800' : 
                          model.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-green-100 text-green-800'
                        }`}>
                          {model.risk}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{model.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Warning Signs */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Warning Signs You Need Service</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
                  Timing Chain Issues
                </h3>
                <ul className="space-y-2">
                  {warningSignsChain.map((sign, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span className="text-gray-700">{sign}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600 mr-2" />
                  Timing Belt Issues
                </h3>
                <ul className="space-y-2">
                  {warningSignsBelt.map((sign, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-orange-600 mr-2">•</span>
                      <span className="text-gray-700">{sign}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-gray-800">
                <strong className="text-yellow-800">⚠️ Important:</strong> If you hear rattling from your engine, 
                especially on cold starts, stop driving immediately and have it inspected. Continued driving can 
                cause catastrophic engine damage costing $10,000+.
              </p>
            </div>
          </motion.div>

          {/* Service Process */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Service Process</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceProcess.map((step, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 relative">
                  <div className="absolute top-4 right-4 w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 pr-16">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Pricing Guide */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Transparent Pricing Guide</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {pricingGuide.map((category, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-semibold mb-2">{category.category}</h3>
                  <p className="text-3xl font-bold text-red-600 mb-4">{category.priceRange}</p>
                  <p className="text-sm text-gray-600 mb-3">Includes:</p>
                  <ul className="space-y-1">
                    {category.includes.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <p className="text-center text-gray-600 mt-6 italic">
              *Prices vary by vehicle make, model, and engine type. We provide exact quotes after inspection.
            </p>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div {...fadeInUp} className="mb-16 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Why Choose Paradise-Performance?</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>European Specialists:</strong> Factory-trained technicians who know your vehicle inside and out
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>OEM & Quality Parts:</strong> We use original or equivalent quality parts only
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Warranty Protection:</strong> 2-year/24,000-mile warranty on parts and labor
                    </div>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Complete Service:</strong> We replace all related components to prevent repeat failures
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Transparent Pricing:</strong> Detailed quotes with no hidden fees
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
                    <div>
                      <strong>Save 30-40%:</strong> Compared to dealership pricing
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeInUp} className="text-center bg-red-600 text-white rounded-xl p-12">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Don't Risk Engine Failure</h2>
            <p className="text-xl mb-8">
              Schedule your timing system inspection today. Prevention costs thousands less than engine replacement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:8173822316" 
                className="inline-block px-8 py-4 bg-white text-red-600 font-bold rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Phone className="inline-block w-5 h-5 mr-2" />
                Call (817) 382-2316
              </a>
              <Link 
                to="/contact" 
                className="inline-block px-8 py-4 bg-red-800 text-white font-bold rounded-lg hover:bg-red-700 transition-colors"
              >
                Get Free Inspection
              </Link>
            </div>
            
            <div className="mt-8 pt-8 border-t border-red-500">
              <p className="text-red-100">
                <MapPin className="inline-block w-4 h-4 mr-1" />
                305 Co Rd 3470, Paradise, TX 76073 • Serving all of Dallas-Fort Worth
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default TimingChainBeltReplacement;
